import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, mediaUrl, type ArticleListItem } from '../lib/api';
import { ARTICLE_CATEGORIES, ARTICLE_STATUS, ago, euro, isRecent, plural } from '../lib/domain';
import { stripMd } from '../lib/md';
import { filterArticles, type ArticleSort } from '../lib/lists';
import { useMe } from '../hooks';
import { ErrorBox, Icon, Loading, PageHead } from '../ui';

type Sort = ArticleSort;
export default function Flohmarkt() {
  const { data: me } = useMe();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [mine, setMine] = useState(false);
  const [hideSold, setHideSold] = useState(true);
  const [sort, setSort] = useState<Sort>('new');
  const { data, isLoading, error } = useQuery({
    queryKey: ['articles'],
    queryFn: () => api<ArticleListItem[]>('/articles'),
  });
  const list = useMemo(
    () => filterArticles(data ?? [], { q, cat, mine, hideSold, sort, me: me?.id }),
    [data, q, cat, mine, hideSold, sort, me?.id],
  );
  return (
    <>
      <PageHead
        title="Flohmarkt"
        right={
          <Link className="btn btn-primary" to="/flohmarkt/neu">
            <Icon name="plus" sw={2.2} />
            Anzeige aufgeben
          </Link>
        }
      />
      <div className="toolbar" role="search">
        <label className="field">
          <Icon name="search" sw={1.8} />
          <input
            type="search"
            placeholder="Suchen: Neo, Laufräder, Größe 42 …"
            value={q}
            onChange={e => setQ(e.target.value)}
            aria-label="Flohmarkt durchsuchen"
          />
        </label>
        <div className="seg" role="group" aria-label="Anzeigen">
          <button aria-pressed={!mine} onClick={() => setMine(false)}>
            Alle
          </button>
          <button aria-pressed={mine} onClick={() => setMine(true)}>
            Meine
          </button>
        </div>
        <select className="select" value={sort} onChange={e => setSort(e.target.value as Sort)} aria-label="Sortierung">
          <option value="new">Neueste zuerst</option>
          <option value="asc">Preis aufsteigend</option>
          <option value="desc">Preis absteigend</option>
        </select>
        <button className="toggle" aria-pressed={hideSold} onClick={() => setHideSold(!hideSold)}>
          <Icon name="ban" sw={1.8} />
          Verkaufte ausblenden
        </button>
        <div className="chips" aria-label="Kategorien">
          {['', ...ARTICLE_CATEGORIES].map(c => (
            <button
              key={c || 'alle'}
              className="chip"
              style={{ ['--dc' as string]: 'var(--navy)' }}
              aria-pressed={cat === c}
              onClick={() => setCat(c)}
            >
              <i />
              {c || 'Alle Kategorien'}
            </button>
          ))}
        </div>
      </div>
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorBox error={error} />
      ) : (
        <>
          <div className="resbar">
            <span>{plural(list.length, 'Anzeige', 'Anzeigen')}</span>
          </div>
          {list.length ? (
            <div className="fgrid">
              {list.map(a => (
                <ArticleCard key={a.id} a={a} />
              ))}
            </div>
          ) : (
            <div className="empty">
              <b>Keine Anzeigen gefunden.</b>
              <p>{mine ? 'Du hast noch keine Anzeige aufgegeben.' : 'Probiere einen anderen Suchbegriff.'}</p>
              <Link className="btn btn-primary" to="/flohmarkt/neu">
                <Icon name="plus" sw={2} />
                Anzeige aufgeben
              </Link>
            </div>
          )}
        </>
      )}
    </>
  );
}

function ArticleCard({ a }: { a: ArticleListItem }) {
  const isNew = isRecent(a.created_at, 14);
  return (
    <Link className="fcard" to={`/flohmarkt/${a.id}`} title={stripMd(a.description).slice(0, 400)}>
      <div className="ph-img">
        {a.status !== 'active' ? (
          <span className={`st ${a.status === 'sold' ? 'st-sold' : 'st-res'}`}>{ARTICLE_STATUS[a.status]}</span>
        ) : (
          isNew && <span className="st st-new">Neu</span>
        )}
        {a.thumb ? (
          <img className="loaded" src={mediaUrl(a.thumb)!} alt={a.title} loading="lazy" decoding="async" />
        ) : (
          <Icon name="cam" sw={1.4} />
        )}
        {a.images > 1 && <span className="np">{a.images} Fotos</span>}
      </div>
      <div className="fbody">
        <div className="price">
          {a.price !== null ? euro(a.price) : <small style={{ margin: 0, fontSize: 13 }}>Preis auf Anfrage</small>}
          {a.negotiable && a.price !== null && <small>VB</small>}
        </div>
        <h3>{a.title}</h3>
        <div className="fmeta">
          <span>{a.category}</span>
          {a.size && (
            <>
              <span>·</span>
              <span>Gr. {a.size}</span>
            </>
          )}
          <span>·</span>
          <span>{a.seller?.name ?? 'unbekannt'}</span>
          <span>·</span>
          <span>{ago(new Date(a.created_at))}</span>
        </div>
      </div>
    </Link>
  );
}
